import React, { Dispatch, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
    FormControl,
    FormLabel,
    MenuItem,
    Select,
    SelectChangeEvent,
    Stack,
    SxProps,
    TextField,
} from '@mui/material';
import { bodyFont, labelFontSemiBold, vars } from '../../../../../Theme';
import { useSelector } from 'react-redux';
import { IAppReduxStore } from '../../../../../redux/store';
import {
    IUploadAdditionalDocumentErrorState,
    IUploadAdditionalDocumentMainState,
} from '../UploadAdditionalDocumentDialog';
import dayjs from 'dayjs';
import { dayjsFormatDate } from '../../../../../Constants';
import UploadFile from '../../_parts/UploadFile';
import {
    appendFilesInfo,
    removeFile,
} from '../../../../../redux/project/documents/sliceProjectUploadAdditionalDocuments';
import { getFileTypeOrDefault } from '../../../../../util/FileUtils';
import { mapFileToRelatedDocument } from '../../../../../redux/import/sliceImportFileUpload';
import { allPropertiesSame } from '../../../../../util/DataUtils';

/** Styles for FormLabel. */
const formLabelSx: SxProps = {
    ...bodyFont,
    fontSize: '16px',
    color: vars.black,
    fontWeight: 700,
    marginBottom: '2px',

    '&.Mui-focused': {
        color: vars.black,
    },
};

const textFieldSx: SxProps = {
    input: { ...bodyFont, p: 0 },
    '& .MuiOutlinedInput-root': {
        '&.Mui-focused fieldset': {
            border: `2px solid ${vars.black}`,
        },
    },
    '& .MuiOutlinedInput-root.MuiInputBase-sizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const selectSx: SxProps = {
    '& .MuiInputBase-inputSizeSmall': {
        py: '6.5px',
        pl: '12px',
    },
};

const optionSx: SxProps = {
    ...labelFontSemiBold,
    fontSize: '16px',
    color: vars.font,
    fontWeight: 500,
};

export default ({
    mainState,
    handleChange,
    showErrorState,
    setShowErrorState,
}: IUploadAdditionalDocumentContentProps) => {
    const { t } = useTranslation('translations', { keyPrefix: 'uploadAdditionalDocumentDialog' });

    const { documentTypeOptions, fileInfo } = useSelector(
        (_store: IAppReduxStore) => _store.projectUploadAdditionalDocuments,
    );
    const fileTypes = useSelector((_store: IAppReduxStore) => _store.projectFileTypes.data);

    const [selectedDocType, setSelectedDocType] = useState<string>(mainState.selectedDocTypeName);
    const [selectedDocVariant, setSelectedDocVariant] = useState<string>(
        mainState.selectedDocTypeVariant,
    );

    const currentDocTypes = useMemo(() => {
        return documentTypeOptions.filter(item => item.docTypeName === selectedDocType);
    }, [documentTypeOptions, selectedDocType]);

    useEffect(() => {
        if (allPropertiesSame(documentTypeOptions, 'docTypeName')) {
            setSelectedDocType(documentTypeOptions[0].docTypeName);
        }
    }, [documentTypeOptions]);

    useEffect(() => {
        handleChange({
            field: 'selectedDocTypeName',
            value: selectedDocType,
        });

        if (isOneDocVariant) {
            const firstDocVariant = currentDocTypes[0].docVariant;
            setSelectedDocVariant(currentDocTypes[0].docVariant);

            handleChange({
                field: 'selectedDocTypeVariant',
                value: firstDocVariant,
            });
        }
    }, [selectedDocType]);

    const uploadedDocument = useMemo(() => {
        if (!fileInfo) {
            return null;
        }
        const fileType = getFileTypeOrDefault(fileInfo.fileName);
        return mapFileToRelatedDocument(fileInfo, fileType);
    }, [fileTypes, fileInfo]);

    const documentVariantItems = useMemo(() => {
        return currentDocTypes.map(item => {
            return (
                <MenuItem key={item.docVariant} value={item.docVariant} sx={optionSx}>
                    {item.docVariant}
                </MenuItem>
            );
        });
    }, [currentDocTypes]);

    const documentTypeItems = useMemo(() => {
        return documentTypeOptions
            .filter(
                (documentType, index, self) =>
                    index === self.findIndex(t => t.docTypeName === documentType.docTypeName),
            )
            .sort((a, b) => a.docTypeName.localeCompare(b.docTypeName))
            .map(item => {
                return (
                    <MenuItem key={item.docTypeName} value={item.docTypeName} sx={optionSx}>
                        {item.docTypeName}
                    </MenuItem>
                );
            });
    }, [documentTypeOptions]);

    const isOneDocVariant = useMemo(() => {
        return currentDocTypes.length === 1;
    }, [currentDocTypes]);

    return (
        <Stack spacing={'12px'}>
            <FormControl
                disabled={allPropertiesSame(documentTypeOptions, 'docTypeName')}
                size='small'
            >
                <FormLabel sx={{ ...formLabelSx, marginTop: 0 }}>
                    {t('selectDocumentType')}
                </FormLabel>
                <Select
                    value={selectedDocType}
                    error={showErrorState.selectedDocTypeVariant}
                    size='small'
                    sx={{
                        ...selectSx,
                        '& .MuiSelect-icon': {
                            display: allPropertiesSame(documentTypeOptions, 'docTypeName')
                                ? 'none'
                                : 'block',
                        },
                    }}
                    onChange={(e: SelectChangeEvent<string>) => {
                        setSelectedDocType(e.target.value);
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                >
                    {documentTypeItems}
                </Select>
            </FormControl>
            <FormControl disabled={!selectedDocType || isOneDocVariant} size='small'>
                <FormLabel sx={{ ...formLabelSx, marginTop: 0 }}>
                    {t('selectAdditionalDocType')}
                </FormLabel>
                <Select
                    value={selectedDocVariant}
                    error={showErrorState.selectedDocTypeVariant}
                    size='small'
                    sx={{
                        ...selectSx,
                        '& .MuiSelect-icon': {
                            display: !selectedDocType || isOneDocVariant ? 'none' : 'block',
                        },
                    }}
                    onChange={(e: SelectChangeEvent<string>) => {
                        setSelectedDocVariant(e.target.value);
                        handleChange({
                            field: 'selectedDocTypeVariant',
                            value: e.target.value,
                        });
                    }}
                    displayEmpty
                    inputProps={{ 'aria-label': 'Without label' }}
                >
                    {documentVariantItems}
                </Select>
            </FormControl>
            <UploadFile
                showError={showErrorState.file}
                fileInfo={fileInfo}
                formLabel={t('fileAttachment')}
                buttonLabel={t('addNewItem')}
                acceptFileTypes='.pdf,.doc,.docx,.dot,.dotx'
                appendFilesInfo={appendFilesInfo}
                removeFile={removeFile}
                uploadedDocuments={[uploadedDocument]}
            />
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('subject')}</FormLabel>
                <TextField
                    sx={textFieldSx}
                    size='small'
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                        handleChange({ field: 'subject', value: e.target.value })
                    }
                    value={mainState.subject}
                    error={showErrorState.subject}
                />
            </FormControl>
            <FormControl>
                <FormLabel sx={formLabelSx}>{t('date')}</FormLabel>
                <TextField
                    disabled
                    size='small'
                    value={dayjs().format(dayjsFormatDate)}
                    sx={textFieldSx}
                />
            </FormControl>
        </Stack>
    );
};

interface IUploadAdditionalDocumentContentProps {
    mainState: IUploadAdditionalDocumentMainState;
    handleChange: ({ field, value }: { field: string; value: string | number }) => void;
    showErrorState: IUploadAdditionalDocumentErrorState;
    setShowErrorState: Dispatch<React.SetStateAction<IUploadAdditionalDocumentErrorState>>;
}
